/**
 * Render a look template to an offscreen canvas with all its layers
 * (background, ref photo, overlay, text) and return a Promise<Image>.
 *
 * Used by collage editors to show referenced looks exactly as they appear
 * in thumbnail previews, not just the raw ref photo.
 */
import { loadImage } from './imageCache';
import { getRefImage } from './thumbnailCache';

const renderLookComposite = async (look, width, height, _allLooks) => {
  if (!look) return null;

  const lookW = look.canvas_width || 2400;
  const lookH = look.canvas_height || 3000;
  const lookAspect = lookW / lookH;
  const containerAspect = width / height;

  let cw, ch;
  if (lookAspect > containerAspect) { cw = width; ch = Math.round(width / lookAspect); }
  else { ch = height; cw = Math.round(height * lookAspect); }

  // Load all assets in parallel
  const refSrc = look.ref_photo_data || look.ref_photo_thumb;
  const overlayKey = (look.overlay_s3_path && look.overlay_filename)
    ? look.overlay_s3_path + (look.overlay_s3_path.endsWith('/') ? '' : '/') + look.overlay_filename
    : null;
  const bgKey = (look.canvas_filepath && look.canvas_filename)
    ? look.canvas_filepath + (look.canvas_filepath.endsWith('/') ? '' : '/') + look.canvas_filename
    : null;

  const [refImg, overlayImg, bgImg, bgRefImg] = await Promise.all([
    refSrc ? getRefImage(refSrc) : Promise.resolve(null),
    overlayKey ? loadImage(overlayKey).catch(() => null) : Promise.resolve(null),
    bgKey ? loadImage(bgKey).catch(() => null) : Promise.resolve(null),
    look.canvas_bg_ref_data ? getRefImage(look.canvas_bg_ref_data) : Promise.resolve(null),
  ]);

  // Draw onto offscreen canvas
  const canvas = document.createElement('canvas');
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext('2d');

  // Transparent background — composites are drawn onto collage canvases
  // that already have their own background
  ctx.clearRect(0, 0, cw, ch);

  // Background and ref photo are always-present implicit layers
  if (bgRefImg) { ctx.drawImage(bgRefImg, 0, 0, cw, ch); }
  else if (bgImg) { ctx.drawImage(bgImg, 0, 0, cw, ch); }

  if (refImg) {
    const imgAspect = refImg.width / refImg.height;
    const canAspect = cw / ch;
    const refZoom = look.ref_zoom ?? 1;
    const refRotation = look.ref_rotation ?? 0;
    const refOffsetX = look.ref_offset_x ?? 0;
    const refOffsetY = look.ref_offset_y ?? 0;

    let dw, dh;
    if (imgAspect > canAspect) { dh = ch; dw = dh * imgAspect; }
    else { dw = cw; dh = dw / imgAspect; }
    dw *= refZoom;
    dh *= refZoom;

    const cx = cw / 2 + refOffsetX * cw;
    const cy = ch / 2 + refOffsetY * ch;

    ctx.save();
    ctx.translate(cx, cy);
    if (refRotation !== 0) ctx.rotate(refRotation * Math.PI / 180);
    ctx.drawImage(refImg, -dw / 2, -dh / 2, dw, dh);
    ctx.restore();
  }

  // Interactive layers from layer_order
  const layerOrder = (look.layer_order && look.layer_order.length > 0)
    ? look.layer_order
    : [{ type: 'overlay' }, { type: 'text' }];

  let overlayDrawn = false, textDrawn = false;

  layerOrder.forEach(layer => {
    if (layer.type === 'overlay' && overlayImg) {
      ctx.drawImage(overlayImg, 0, 0, cw, ch);
      overlayDrawn = true;
    }

    if (layer.type === 'text') {
      const drawTf = (tf) => {
        if (tf.center_x != null && tf.center_y != null) {
          const tx = tf.center_x * cw, ty = tf.center_y * ch;
          const left = (tf.bound_x_left || 0) * cw, right = (tf.bound_x_right || 1) * cw;
          const fs = Math.min(40, Math.max(8, (tf.font_size_max || 160) * ch / (look.canvas_height || 3000)));
          const familyName = tf.font_filename ? tf.font_filename.replace(/\.[^.]+$/, '') : 'sans-serif';
          ctx.font = `${tf.italic ? 'italic ' : ''}${tf.bold ? 'bold ' : ''}${fs}px ${familyName}, sans-serif`;
          ctx.fillStyle = tf.font_color || '#000';
          if (tf.shadow) { ctx.shadowColor = 'rgba(0,0,0,0.5)'; ctx.shadowBlur = 2; ctx.shadowOffsetX = 1; ctx.shadowOffsetY = 1; }
          ctx.textAlign = tf.align || 'center';
          const ax = tf.align === 'left' ? left + 2 : tf.align === 'right' ? right - 2 : tx;
          ctx.fillText(tf.content || '', ax, ty + fs * 0.35);
          ctx.textAlign = 'start';
          if (tf.shadow) { ctx.shadowColor = 'transparent'; ctx.shadowBlur = 0; ctx.shadowOffsetX = 0; ctx.shadowOffsetY = 0; }
        }
      };
      if (layer.index != null) {
        const tf = (look.text_fields || [])[layer.index];
        if (tf) drawTf(tf);
      } else {
        (look.text_fields || []).forEach(drawTf);
      }
      textDrawn = true;
    }
  });

  // Fallback for elements not in layer_order
  if (!overlayDrawn && overlayImg) { ctx.drawImage(overlayImg, 0, 0, cw, ch); }
  if (!textDrawn && (look.text_fields || []).length > 0) {
    (look.text_fields || []).forEach(tf => {
      if (tf.center_x != null && tf.center_y != null) {
        const fs = Math.min(40, Math.max(8, (tf.font_size_max || 160) * ch / (look.canvas_height || 3000)));
        const familyName = tf.font_filename ? tf.font_filename.replace(/\.[^.]+$/, '') : 'sans-serif';
        ctx.font = `${tf.italic ? 'italic ' : ''}${tf.bold ? 'bold ' : ''}${fs}px ${familyName}, sans-serif`;
        ctx.fillStyle = tf.font_color || '#000';
        ctx.textAlign = tf.align || 'center';
        const tx = tf.center_x * cw, ty = tf.center_y * ch;
        const left = (tf.bound_x_left || 0) * cw, right = (tf.bound_x_right || 1) * cw;
        const ax = tf.align === 'left' ? left + 2 : tf.align === 'right' ? right - 2 : tx;
        ctx.fillText(tf.content || '', ax, ty + fs * 0.35);
        ctx.textAlign = 'start';
      }
    });
  }

  // Convert to Image
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = canvas.toDataURL('image/png');
  });
};

export default renderLookComposite;
